import type {INetsMap, IPropertiesMap} from "@buildwithflux/models";
import {useShallow} from "zustand/react/shallow";

import {useFluxServices} from "../../../../injection/hooks";
import {select} from "../selectors";

/**
 * Hook to get all nets
 * Uses shallow comparison to prevent unnecessary re-renders
 */
export const useAllNets = (): INetsMap | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.nets.all));
};

/**
 * Hook to get a net by UID
 * @param uid Net UID to retrieve
 */
export const useNetByUid = (uid: string): INetsMap[string] | undefined => {
    return useFluxServices().useDocumentStore(select.nets.byUid(uid));
};

/**
 * Hook to get multiple nets by UIDs
 * @param uids Net UIDs to retrieve
 */
export const useNetsByUids = (uids: string[]): Array<INetsMap[string]> => {
    return useFluxServices().useDocumentStore(useShallow(select.nets.byUids(uids)));
};

/**
 * Hook to get all net UIDs
 */
export const useNetUids = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.nets.uids));
};

/**
 * Hook to get net count
 */
export const useNetCount = (): number => {
    return useFluxServices().useDocumentStore(select.nets.count);
};

/**
 * Hook to check if document has any nets
 */
export const useHasNets = (): boolean => {
    return useFluxServices().useDocumentStore(select.nets.hasNets);
};

/**
 * Hook to get all net labels - memoized
 */
export const useNetLabels = (): string[] => {
    return useFluxServices().useDocumentStore(useShallow(select.nets.labels));
};

/**
 * Hook to get count of net labels
 * Returns a primitive number to avoid useShallow overhead
 * Use this instead of useNetLabels().length when only the count is needed
 */
export const useNetLabelsCount = (): number => {
    return useFluxServices().useDocumentStore(select.nets.labelsCount);
};

/**
 * Hook to get net properties by UID
 * @param netUid - The net UID
 */
export const useNetProperties = (netUid: string): IPropertiesMap | undefined => {
    return useFluxServices().useDocumentStore(useShallow(select.nets.propertiesByUid(netUid)));
};

/**
 * Hook to get the label of the first selected net
 * @param selectedObjectUids - Array of selected object UIDs
 */
export const useNetLabel = (selectedObjectUids: string[]): string => {
    return useFluxServices().useDocumentStore(select.nets.firstLabelByUids(selectedObjectUids));
};

/**
 * Hook to count selected nets
 * @param selectedObjectUids - Array of selected object UIDs
 */
export const useSelectedNetsCount = (selectedObjectUids?: string[]): number => {
    return useFluxServices().useDocumentStore(select.nets.selectedCount(selectedObjectUids));
};
